/**
 * Wire codecs. CBOR is the default; JSON (with base64url bytes) is for debugging.
 * Both produce a canonical-enough encoding for the purpose of signing `hello`:
 * signatures are always computed over the CBOR form with keys sorted.
 */
import { Encoder } from "cbor-x";
import { AnyMessage } from "./messages.js";
const cbor = new Encoder({ useRecords: false, mapsAsObjects: true, tagUint8Array: false });
function sortKeys(v) {
    if (v instanceof Uint8Array || v === null || typeof v !== "object")
        return v;
    if (Array.isArray(v))
        return v.map(sortKeys);
    const o = v;
    const out = {};
    for (const k of Object.keys(o).sort()) {
        if (o[k] !== undefined)
            out[k] = sortKeys(o[k]);
    }
    return out;
}
export function encodeCbor(msg) {
    return cbor.encode(sortKeys(msg));
}
export function decodeCbor(buf) {
    const raw = cbor.decode(buf);
    return AnyMessage.parse(normalizeBytes(raw));
}
/** cbor-x may return Buffer subclasses; normalise to plain Uint8Array for zod instanceof checks. */
function normalizeBytes(v) {
    if (v instanceof Uint8Array)
        return new Uint8Array(v.buffer, v.byteOffset, v.byteLength);
    if (Array.isArray(v))
        return v.map(normalizeBytes);
    if (v && typeof v === "object") {
        const o = v;
        const out = {};
        for (const k of Object.keys(o))
            out[k] = normalizeBytes(o[k]);
        return out;
    }
    return v;
}
const B64 = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";
function b64u(bytes) {
    let out = "";
    let i = 0;
    for (; i + 2 < bytes.length; i += 3) {
        const n = (bytes[i] << 16) | (bytes[i + 1] << 8) | bytes[i + 2];
        out += B64[(n >> 18) & 63] + B64[(n >> 12) & 63] + B64[(n >> 6) & 63] + B64[n & 63];
    }
    if (i < bytes.length) {
        const rem = bytes.length - i;
        const n = (bytes[i] << 16) | (rem === 2 ? bytes[i + 1] << 8 : 0);
        out += B64[(n >> 18) & 63] + B64[(n >> 12) & 63];
        if (rem === 2)
            out += B64[(n >> 6) & 63];
    }
    return out;
}
function unb64u(s) {
    const lookup = {};
    for (let i = 0; i < B64.length; i++)
        lookup[B64.charAt(i)] = i;
    const out = new Uint8Array(Math.floor((s.length * 3) / 4));
    let o = 0;
    let i = 0;
    for (; i + 3 < s.length; i += 4) {
        const n = (lookup[s.charAt(i)] << 18) |
            (lookup[s.charAt(i + 1)] << 12) |
            (lookup[s.charAt(i + 2)] << 6) |
            lookup[s.charAt(i + 3)];
        out[o++] = (n >> 16) & 255;
        out[o++] = (n >> 8) & 255;
        out[o++] = n & 255;
    }
    const rem = s.length - i;
    if (rem >= 2) {
        const n = (lookup[s.charAt(i)] << 18) |
            (lookup[s.charAt(i + 1)] << 12) |
            (rem === 3 ? lookup[s.charAt(i + 2)] << 6 : 0);
        out[o++] = (n >> 16) & 255;
        if (rem === 3)
            out[o++] = (n >> 8) & 255;
    }
    return out;
}
const BYTE_FIELDS = new Set(["sig", "vpk", "body", "wrapped"]);
export function encodeJson(msg) {
    return JSON.stringify(msg, (k, v) => (v instanceof Uint8Array ? { $b: b64u(v) } : v));
}
export function decodeJson(text) {
    const raw = JSON.parse(text, (k, v) => {
        if (v && typeof v === "object" && typeof v.$b === "string" && Object.keys(v).length === 1)
            return unb64u(v.$b);
        if (BYTE_FIELDS.has(k) && typeof v === "string")
            return unb64u(v);
        return v;
    });
    return AnyMessage.parse(raw);
}
export function encode(msg, enc = "cbor") {
    return enc === "cbor" ? encodeCbor(msg) : new TextEncoder().encode(encodeJson(msg));
}
export function decode(buf, enc = "cbor") {
    return enc === "cbor" ? decodeCbor(buf) : decodeJson(new TextDecoder().decode(buf));
}
/** Canonical bytes that a device signs for `hello` (the message without `sig`, CBOR, sorted keys). */
export function helloSignable(hello) {
    const { ...rest } = hello;
    delete rest["sig"];
    return cbor.encode(sortKeys(rest));
}
//# sourceMappingURL=codec.js.map